<script lang="ts">
export default {
  name: 'ViewFilter',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted, reactive } from 'vue';
import { Filter, X } from 'lucide-vue-next';
import { FilterField, FilterFieldOption, FilterMoreOptions, FilterRange, FilterValueType } from '@/types/ViewFilter.js';
import { LabelFilterType } from '@/types/components/FilterType.js';
import NumberInput from '@/components/NumberInput.vue';
import { compactNumber } from '@/utilities/Filters.js';

interface _LabelValue<FilterValueType> extends LabelFilterType<FilterValueType> {
  category: string;
  range?: FilterRange;
}

interface _FilterField<FilterValueType> extends FilterField<FilterValueType> {
  values: _LabelValue<FilterValueType>[];
}

const props = withDefaults(
  defineProps<{
    appliedFilters?: FilterField<FilterValueType>[];
    appliedOptions?: FilterMoreOptions;
    filters: FilterField<FilterValueType>[];
    showMoreOptions?: boolean;
    filterOpen?: boolean;
    activeFilterCategory?: string;
    customActiveFilterBadges?: boolean;
  }>(),
  {
    appliedFilters: undefined,
    showMoreOptions: false,
    customActiveFilterBadges: false,
  },
);

const emit = defineEmits<{
  change: [fields: FilterField<FilterValueType>[], moreOptions?: FilterMoreOptions];
  filterClosed: [];
}>();

// State
const menu = ref(false);
const searchQuery = ref('');
const localSelectedFields = ref<_LabelValue<FilterValueType>[]>([]);
const localSelectedOptions = ref<FilterFieldOption[]>([]);
const dirty = ref(false);
const activeCategory = ref('');
const localFilters = ref<_FilterField<FilterValueType>[]>([]);

const moreOptions = reactive({
  daysUntilExpiration: undefined as number | undefined,
  runoff: false,
  noRunoff: false,
  primary: false,
  excess: false,
} as FilterMoreOptions);

const activeRange = reactive({
  min: 0,
  max: 0,
});

// Computed
const selectedFields = computed(() => props.appliedFilters || []);

const appliedOptionsArray = computed(() => {
  if (!props.appliedOptions) return [];

  return Object.values(props.appliedOptions).filter((o) => o !== undefined);
});

const selectedCount = computed(() => {
  const valueCount = selectedFields.value.reduce((count, field) => count + field.values.length, 0);
  const rangeCount = selectedFields.value.reduce((count, field) => count + (field.range ? 1 : 0), 0);

  let moreOptionsCount = 0;
  if (moreOptions.daysUntilExpiration !== undefined) moreOptionsCount++;
  if (moreOptions.runoff) moreOptionsCount++;
  if (moreOptions.noRunoff) moreOptionsCount++;
  if (moreOptions.primary) moreOptionsCount++;
  if (moreOptions.excess) moreOptionsCount++;

  return valueCount + rangeCount + moreOptionsCount;
});

const localSelectedCount = computed(() => {
  let moreOptionsCount = 0;
  if (moreOptions.daysUntilExpiration !== undefined) moreOptionsCount++;
  if (moreOptions.runoff) moreOptionsCount++;
  if (moreOptions.noRunoff) moreOptionsCount++;
  if (moreOptions.primary) moreOptionsCount++;
  if (moreOptions.excess) moreOptionsCount++;

  return localSelectedFields.value.length + moreOptionsCount;
});

const categories = computed(() => {
  return [...new Set(props.filters.map((f) => f.category))];
});

const activeFilter = computed(() => localFilters.value.find((f) => f.category === activeCategory.value));

const activeFilterDescription = computed(() => activeFilter.value?.description ?? '');

const activeFilterOptions = computed(() => activeFilter.value?.options || []);

const activeFilterPrefix = computed(() => activeFilter.value?.prefix ?? '');

const filteredActiveFilterValues = computed(() => {
  const values = activeFilter.value?.values || [];

  if (!searchQuery.value) return values || [];

  const query = searchQuery.value.toLowerCase();
  return values.filter((field) => field.label.toLowerCase().includes(query));
});

const isActiveFilterRange = computed(() => {
  if (activeFilter.value) {
    return activeFilter.value.range !== undefined;
  }
  return false;
});

const isMoreOptionsActive = computed(() => {
  return activeCategory.value === 'More Options';
});

const moreOptionsCount = computed(() => {
  let moreOptionsCount = 0;
  if (moreOptions.daysUntilExpiration !== undefined) moreOptionsCount++;
  if (moreOptions.runoff) moreOptionsCount++;
  if (moreOptions.noRunoff) moreOptionsCount++;
  if (moreOptions.primary) moreOptionsCount++;
  if (moreOptions.excess) moreOptionsCount++;
  return moreOptionsCount;
});

// Lifecycle

onMounted(() => {
  updateLocalFilters(props.filters);
  updateLocalAppliedFilters();
  updateLocalAppliedFilterOptions();
  updateLocalAppliedMoreOptions();

  searchQuery.value = '';
  activeCategory.value = categories.value[0] || '';
});

// Refs

const searchInputRef = ref<HTMLElement | null>(null);

// Watch
watch(
  () => props.filterOpen,
  (val) => {
    menu.value = val;
  },
);

watch(
  () => props.activeFilterCategory,
  (val) => {
    if (!val) {
      activeCategory.value = categories.value.length > 0 ? categories.value[0] : '';
    } else {
      activeCategory.value = val;
    }
  },
);

watch(menu, async (val) => {
  if (val) {
    // When opening, sync local state with applied fields

    updateLocalAppliedFilters();
    updateLocalAppliedFilterOptions();
    updateLocalAppliedMoreOptions();

    searchQuery.value = '';
  } else {
    emit('filterClosed');
    activeCategory.value = categories.value[0];
  }
});

watch(searchInputRef, () => {
  if (!isActiveFilterRange.value) {
    searchInputRef.value?.focus();
  }
});

watch(localSelectedFields, () => {
  dirty.value = true;
});

watch(localSelectedOptions, () => {
  dirty.value = true;
});

watch(
  () => props.filters,
  (next) => {
    updateLocalFilters(next);
    updateLocalAppliedFilters();
    updateLocalAppliedFilterOptions();
  },
);

watch(
  () => props.appliedOptions,
  () => {
    updateLocalAppliedMoreOptions();
  },
);

watch(activeCategory, () => {
  if (isActiveFilterRange.value) {
    const cat = localSelectedFields.value.find((f) => f.category === activeCategory.value);
    const defaultRange = localFilters.value?.find((f) => f.category === activeCategory.value)?.range;

    activeRange.min = cat?.range?.[0] ?? defaultRange?.[0] ?? 0;
    activeRange.max = cat?.range?.[1] ?? defaultRange?.[1] ?? 0;
  } else {
    searchInputRef.value?.focus();
  }
});

function updateLocalFilters(filtersToWrap: FilterField<FilterValueType>[]) {
  localFilters.value =
    filtersToWrap?.map((f) => ({
      category: f.category,
      description: f.description,
      values: f.values?.map((v) => ({ ...v, category: f.category })) ?? [],
      range: f.range,
      options: f.options,
      prefix: f.prefix,
    })) ?? [];
}

function updateLocalAppliedFilters() {
  localSelectedFields.value = selectedFields.value.flatMap((f) => {
    const availableValues = localFilters.value.find((filter) => filter.category === f.category)?.values || [];

    const selectedValues =
      f.values?.map((appliedValue) => {
        // Find the matching object from available options
        return availableValues.find((available) => available.value === appliedValue.value) || { ...appliedValue, category: f.category };
      }) || [];

    // Handle range filters...
    if (f.range) {
      selectedValues.push({
        label: '',
        value: 0,
        category: f.category,
        range: f.range,
      });
    }

    return selectedValues;
  });
}

function updateLocalAppliedFilterOptions() {
  localSelectedOptions.value = selectedFields.value.flatMap(
    (f) =>
      f.options?.map((o) => ({
        ...o,
        category: f.category,
      })) || [],
  );
}

function updateLocalAppliedMoreOptions() {
  if (!props.appliedOptions) {
    moreOptions.daysUntilExpiration = undefined;
    moreOptions.runoff = undefined;
    moreOptions.noRunoff = undefined;
    moreOptions.primary = undefined;
    moreOptions.excess = undefined;

    return;
  }

  moreOptions.daysUntilExpiration = props.appliedOptions.daysUntilExpiration;
  moreOptions.runoff = props.appliedOptions.runoff;
  moreOptions.noRunoff = props.appliedOptions.noRunoff;
  moreOptions.primary = props.appliedOptions.primary;
  moreOptions.excess = props.appliedOptions.excess;
}

function updateActiveRangeMin(value: number) {
  activeRange.min = value;
  updateActiveCategoryRange();
}

function updateActiveRangeMax(value: number) {
  activeRange.max = value;
  updateActiveCategoryRange();
}

function updateActiveCategoryRange() {
  const filter = activeFilter.value;
  if (!filter) return;
  if (!filter.range) return;

  const selected = localSelectedFields.value.find((f) => f.category === activeCategory.value);

  if (selected) {
    selected.range = [activeRange.min, activeRange.max];
  } else {
    localSelectedFields.value.push({
      label: '',
      value: 0,
      category: filter.category,
      range: [activeRange.min, activeRange.max],
    });
  }

  // find in applied filters (or compare with default values), if the values match, then this range is no longer "dirty"

  let val: FilterRange;

  const passedIn = selectedFields.value.find((f) => f.category === activeCategory.value);
  if (!passedIn || !passedIn.range) {
    val = filter.range;
  } else {
    val = passedIn.range;
  }

  if (val[0] === activeRange.min && val[1] === activeRange.max) {
    // set value matches already applied filter or default range values -> remove it
    const index = localSelectedFields.value.findIndex((f) => f.category === activeCategory.value);
    if (index >= 0) {
      localSelectedFields.value.splice(index, 1);
    }
  }
}

function resetToDefaults() {
  localSelectedFields.value = [];

  moreOptions.daysUntilExpiration = undefined;
  moreOptions.runoff = false;
  moreOptions.noRunoff = false;
  moreOptions.primary = false;
  moreOptions.excess = false;

  // Reset active range to default values if current filter is a range filter
  if (isActiveFilterRange.value) {
    const defaultRange = localFilters.value?.find((f) => f.category === activeCategory.value)?.range;
    activeRange.min = defaultRange?.[0] ?? 0;
    activeRange.max = defaultRange?.[1] ?? 0;
  }
}

function getCategorySelectionCount(category: string) {
  return localSelectedFields.value.filter((f) => f.category === category).length;
}

function cancel() {
  localSelectedFields.value = selectedFields.value.flatMap((f) =>
    f.values.map((g) => ({
      ...g,
      category: f.category,
      selected: false,
    })),
  );
  searchQuery.value = '';
  activeCategory.value = categories.value[0] || '';
  menu.value = false;
}

function apply() {
  if (!dirty.value) return;

  // reconstruct structure from flattened localSelectedFields

  const filterMap = new Map<string, FilterField<FilterValueType>>();

  const categories = localSelectedFields.value.reduce((categories, f) => categories.add(f.category), new Set<string>());

  categories.forEach((c) => {
    filterMap.set(c, {
      category: c,
      values: [],
    });
  });

  // process selectable filters

  localSelectedFields.value.forEach((selected: _LabelValue<FilterValueType>) => {
    const f = filterMap.get(selected.category);

    if (f) {
      if (selected.range) {
        f.range = selected.range;
      } else {
        f.values.push({
          label: selected.label,
          value: selected.value,
        });
      }
    }
  });

  // process options

  localSelectedOptions.value.forEach((option: FilterFieldOption) => {
    const f = filterMap.get(option.category);

    if (f) {
      if (!f.options) f.options = [];
      f.options?.push(option);
    }
  });

  const changed = Array.from(filterMap.values()) as FilterField<FilterValueType>[];

  let filterOptions = {} as FilterMoreOptions;
  filterOptions.daysUntilExpiration = moreOptions.daysUntilExpiration;
  filterOptions.runoff = moreOptions.runoff ? true : undefined;
  filterOptions.noRunoff = moreOptions.noRunoff ? true : undefined;
  filterOptions.primary = moreOptions.primary ? true : undefined;
  filterOptions.excess = moreOptions.excess ? true : undefined;

  let isFilterOptionsUndefined = false;
  if (!Object.values(filterOptions).some((value) => value !== undefined && value !== null && value !== false)) {
    isFilterOptionsUndefined = true;
  }
  emit('change', changed, isFilterOptionsUndefined ? undefined : filterOptions);

  menu.value = false;
}

function handleActiveFilterSelect(category: string) {
  activeCategory.value = category;
  menu.value = true;
}

function handleActiveMoreOptionsSelect() {
  activeCategory.value = 'More Options';
  menu.value = true;
}

function handleRemoveFiltersByCategory(category: string) {
  localSelectedFields.value = localSelectedFields.value.filter((f) => f.category !== category);
  apply();
}

function handleRemoveMoreOptions() {
  const optionKeys = Object.keys(moreOptions);
  for (const key of optionKeys) {
    moreOptions[key] = undefined;
  }
  apply();
}

function calculateFilterGroupCount(filterGroup: FilterField<FilterValueType>) {
  // For range type filters, there is nothing to count, just return a 1 to indicate the active range filter.
  if (filterGroup.range && filterGroup.range.length > 0) {
    return 1;
  } else {
    return filterGroup.values.length;
  }
}
</script>

<template>
  <div class="modern-filter-button tw:flex tw:gap-4 tw:p-1">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      location="bottom"
      origin="left top"
      transition="slide-x-transition">
      <template #activator="{ props }">
        <div
          class="tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-3 tw:py-1.5 tw:select-none tw:hover:bg-gray-50"
          v-bind="props"
          data-testid="view-filter-activator">
          <Filter class="tw:h-4 tw:w-4 tw:stroke-primary" />
          <span class="tw:text-sm tw:font-medium">Filters</span>
          <span
            v-if="selectedCount > 0"
            class="tw:badge tw:border-gray-400 tw:bg-base-200 tw:badge-sm"
            >{{ selectedCount }}</span
          >
        </div>
      </template>

      <div class="mint-filter-popup tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:shadow-xl">
        <!-- Header -->
        <div class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-gray-200 tw:p-4">
          <div class="tw:flex tw:items-center tw:gap-2">
            <h3 class="tw:text-lg tw:font-semibold">Filters</h3>
            <div class="tw:min-w-9">
              <span
                class="tw:inline-flex tw:h-5 tw:min-w-5 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:border-gray-400 tw:bg-base-200 tw:px-1.5 tw:text-xs tw:font-medium"
                :class="{
                  'tw:invisible': localSelectedCount === 0,
                }">
                {{ localSelectedCount }}
              </span>
            </div>
          </div>

          <!-- Search bar -->
          <div class="tw:w-full tw:px-20">
            <div class="tw:relative">
              <svg
                class="tw:absolute tw:top-2.5 tw:left-3 tw:h-4 tw:w-4 tw:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input
                ref="searchInputRef"
                type="text"
                v-model="searchQuery"
                placeholder="Search"
                class="tw:w-full tw:rounded-md tw:border tw:border-solid tw:border-gray-300 tw:bg-gray-50 tw:py-2 tw:pr-3 tw:pl-9 tw:text-sm tw:focus:ring-2 tw:focus:ring-blue-500 tw:focus:outline-none"
                data-testid="column-filter-search" />
            </div>
          </div>

          <div class="tw:flex tw:items-center tw:gap-4">
            <a
              @click="resetToDefaults"
              class="tw:cursor-pointer tw:text-sm tw:text-blue-600 tw:hover:underline"
              data-testid="column-filter-reset">
              Reset
            </a>
          </div>
        </div>

        <div class="tw:flex tw:h-[540px]">
          <!-- Left sidebar -->
          <div
            data-testid="view-filter-category"
            class="tw:w-48 tw:border-r tw:border-gray-200 tw:bg-gray-50 tw:select-none">
            <div class="tw:p-3">
              <div
                v-for="stack in categories"
                :data-testid="`view-filter-category-${stack}`"
                :key="stack"
                @click="activeCategory = stack"
                class="tw:cursor- tw:mb-1 tw:flex tw:cursor-pointer tw:items-center tw:justify-between tw:rounded-md tw:px-3 tw:py-2 tw:text-sm"
                :class="[activeCategory === stack ? 'tw:bg-blue-100 tw:font-medium tw:text-blue-700' : 'tw:text-gray-700 tw:hover:bg-gray-100']">
                <span>{{ stack }}</span>
                <span
                  v-if="getCategorySelectionCount(stack) > 0"
                  class="tw:inline-flex tw:h-5 tw:min-w-5 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:border-gray-400 tw:bg-base-200 tw:px-1.5 tw:text-xs tw:font-medium">
                  {{ getCategorySelectionCount(stack) }}
                </span>
              </div>
              <div
                v-if="props.showMoreOptions"
                @click="activeCategory = 'More Options'"
                class="tw:cursor- tw:mb-1 tw:flex tw:cursor-pointer tw:items-center tw:justify-between tw:rounded-md tw:px-3 tw:py-2 tw:text-sm"
                :class="[
                  activeCategory === 'More Options' ? 'tw:bg-blue-100 tw:font-medium tw:text-blue-700' : 'tw:text-gray-700 tw:hover:bg-gray-100',
                ]">
                <span>More Options</span>
                <span
                  v-if="moreOptionsCount > 0"
                  class="tw:inline-flex tw:h-5 tw:min-w-5 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:border-gray-400 tw:bg-base-200 tw:px-1.5 tw:text-xs tw:font-medium">
                  {{ moreOptionsCount }}
                </span>
              </div>
            </div>
          </div>

          <!-- Right content -->
          <div class="tw:flex tw:flex-1 tw:flex-col">
            <div
              v-if="activeFilterOptions.length > 0"
              class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-gray-200 tw:p-4">
              Options
              <div class="tw:flex tw:items-center tw:gap-2">
                <div
                  v-for="option in activeFilterOptions"
                  :key="option.label"
                  class="tw:flex tw:items-center tw:gap-2">
                  <input
                    type="checkbox"
                    v-model="localSelectedOptions"
                    :value="option"
                    class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-primary tw:toggle-sm" />
                  <label
                    for="includeChildren"
                    class="tw:text-sm">
                    {{ option.label }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Content area -->
            <div class="tw:flex-1 tw:overflow-y-auto tw:p-4">
              <template v-if="isActiveFilterRange">
                <div>
                  {{ activeFilterDescription }}
                </div>

                <div class="tw:mt-8 tw:flex tw:w-1/2 tw:flex-col tw:gap-8">
                  <NumberInput
                    label="Minimum"
                    :modelValue="activeRange.min"
                    @update:modelValue="
                      (val) => {
                        updateActiveRangeMin(Number(val));
                      }
                    "
                    outlined
                    persistent-hint
                    :prefix="activeFilterPrefix"
                    :hint="compactNumber(activeRange.min)" />

                  <NumberInput
                    label="Maximum"
                    :modelValue="activeRange.max"
                    @update:modelValue="
                      (val) => {
                        updateActiveRangeMax(Number(val));
                      }
                    "
                    outlined
                    persistent-hint
                    :prefix="activeFilterPrefix"
                    :hint="compactNumber(activeRange.max)" />
                </div>
              </template>

              <template v-if="isMoreOptionsActive">
                <label class="tw:flex tw:flex-col tw:gap-3 tw:rounded-md tw:p-2 tw:select-none">
                  <span class="tw:font-bold">Days Until Expiration</span>
                  <input
                    type="text"
                    v-model="moreOptions.daysUntilExpiration"
                    class="tw:input tw:input-sm tw:w-20 tw:border tw:border-solid tw:border-gray-300" />
                </label>

                <div class="tw:divider tw:my-2"></div>

                <div class="tw:font-bold">Runoff</div>

                <div class="tw:flex">
                  <label class="tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:p-2 tw:hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.runoff"
                      class="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300" />

                    <span class="tw:text-sm tw:text-gray-700 tw:select-none"> Runoff </span>
                  </label>

                  <label class="tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:p-2 tw:hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.noRunoff"
                      class="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300" />

                    <span class="tw:text-sm tw:text-gray-700 tw:select-none"> No Runoff </span>
                  </label>
                </div>

                <div class="tw:divider"></div>

                <div class="tw:font-bold tw:select-none">Primary / Excess</div>

                <div class="tw:flex">
                  <label class="tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:p-2 tw:hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.primary"
                      class="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300" />

                    <div class="tw:flex tw:flex-1 tw:items-center tw:gap-2">
                      <span class="tw:text-sm tw:text-gray-700 tw:select-none"> Primary </span>
                    </div>
                  </label>

                  <label class="tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:p-2 tw:hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.excess"
                      class="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300" />

                    <div class="tw:flex tw:flex-1 tw:items-center tw:gap-2">
                      <span class="tw:text-sm tw:text-gray-700 tw:select-none"> Excess </span>
                    </div>
                  </label>
                </div>
              </template>

              <template v-else>
                <!-- Fields list -->
                <label
                  v-for="field in filteredActiveFilterValues"
                  :key="field.value"
                  class="tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:p-2 tw:hover:bg-gray-50">
                  <input
                    type="checkbox"
                    v-model="localSelectedFields"
                    :value="field"
                    class="tw:h-4 tw:w-4 tw:rounded tw:border-gray-300" />
                  <div class="tw:flex tw:flex-1 tw:items-center tw:gap-2">
                    <span class="tw:text-sm tw:text-gray-700">{{ field.label }}</span>
                  </div>
                </label>

                <!-- No results -->
                <div
                  v-if="!isActiveFilterRange && filteredActiveFilterValues.length === 0"
                  class="tw:py-8 tw:text-center tw:text-gray-500">
                  No matches found
                </div>
              </template>
            </div>

            <!-- Footer -->
            <div class="tw:flex tw:justify-end tw:gap-3 tw:border-t tw:border-gray-200 tw:p-4">
              <button
                @click="cancel"
                class="tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-700 tw:hover:bg-gray-50">
                Cancel
              </button>
              <button
                @click="apply"
                class="tw:rounded-md tw:bg-primary tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-white tw:opacity-90 tw:transition-colors tw:hover:opacity-100">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </v-menu>
    <slot
      v-if="!customActiveFilterBadges"
      name="activeFilters"
      :appliedFilters="appliedFilters"
      :appliedOptions="moreOptions">
      <div
        v-for="filterGroup in appliedFilters"
        :key="filterGroup.category"
        @click="handleActiveFilterSelect(filterGroup.category)"
        class="tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-secondary tw:bg-white tw:px-3 tw:py-1.5 tw:whitespace-nowrap tw:transition-all tw:select-none tw:hover:bg-gray-50"
        :data-testid="`view-filter-badge-${filterGroup.category}`">
        {{ filterGroup.category }}
        <div class="tw:badge tw:border-gray-400 tw:bg-base-200 tw:badge-sm">
          {{ calculateFilterGroupCount(filterGroup) }}
        </div>
        <X
          :size="15"
          class="tw:stroke-gray-600 tw:transition-colors tw:hover:stroke-red-500"
          @click.stop="handleRemoveFiltersByCategory(filterGroup.category)" />
      </div>
      <div
        v-if="appliedOptionsArray.length > 0"
        @click="handleActiveMoreOptionsSelect()"
        class="tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-secondary tw:bg-white tw:px-3 tw:py-1.5 tw:transition-all tw:select-none tw:hover:bg-gray-50">
        More Options
        <div class="tw:badge tw:border-gray-400 tw:bg-base-200">{{ appliedOptionsArray.length }}</div>
        <X
          :size="15"
          class="tw:stroke-gray-600 tw:transition-colors tw:hover:stroke-red-500"
          @click.stop="handleRemoveMoreOptions()" />
      </div>
    </slot>
  </div>
</template>

<style scoped>
.modern-filter-button {
  display: inline-block;
}

.mint-filter-popup {
  width: 650px;
}

/* Customize checkbox appearance */
input[type='checkbox'] {
  cursor: pointer;
}

input[type='checkbox']:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

input[type='checkbox']:indeterminate {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 16 16'%3e%3cpath d='M5 8h6'/%3e%3c/svg%3e");
  background-color: #3b82f6;
  border-color: #3b82f6;
}
</style>
